import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Badge, { type BadgeProps } from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import type { Alert, AlertSeverity } from '@etiquetachef/types';
import { AcknowledgeButton } from './AcknowledgeButton';

const SEVERITY_COLOR: Record<AlertSeverity, NonNullable<BadgeProps['color']>> = {
  HIGH: 'red',
  MEDIUM: 'blue',
  LOW: 'grey',
};

function formatDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 16).replace('T', ' ');
}

/**
 * Cartão de um alerta de BI (tipo, severidade, período, detalhe) com o botão de
 * reconhecer; datas em UTC.
 */
export function AlertCard({ alert }: { alert: Alert }): React.JSX.Element {
  const { t } = useTranslation();
  const period =
    alert.periodAffected.fromYyyymmdd +
    (alert.periodAffected.fromYyyymmdd !== alert.periodAffected.toYyyymmdd
      ? ` – ${alert.periodAffected.toYyyymmdd}`
      : '');
  return (
    <Container
      header={
        <Header
          variant="h3"
          description={`${formatDate(alert.observedAt)} · ${period}`}
          actions={
            <AcknowledgeButton
              alertId={alert.alertId}
              alertKind={alert.kind}
              observedAtMs={alert.observedAt}
              acknowledgedAt={alert.acknowledgedAt}
            />
          }
        >
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Badge color={SEVERITY_COLOR[alert.severity]}>
              {t(`alerts.severity.${alert.severity}`)}
            </Badge>
            <span>{t(`alerts.kind.${alert.kind}`)}</span>
          </SpaceBetween>
        </Header>
      }
    >
      <SpaceBetween size="xxs">
        <Box>{alert.detail}</Box>
        <Box fontSize="body-s" color="text-status-inactive">
          {t('alerts.magnitude')}: {alert.magnitude.toFixed(3)}
          {alert.emailSent ? ` · ${t('alerts.emailSent')}` : ''}
        </Box>
        {alert.acknowledgedAt !== null && (
          <Box fontSize="body-s" color="text-status-inactive">
            {t('alerts.acknowledgedBy', {
              who: alert.acknowledgedBy ?? '?',
              when: formatDate(alert.acknowledgedAt),
            })}
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
}
